import type { NewPushSubscription } from "@larder/shared";
import type { PushPermission } from "./ask";

/**
 * The browser side of Web Push: capability, permission, subscription.
 *
 * Everything impure lives here so that `ask.ts` — which decides *whether* to
 * ask — stays a pure function with tests. This file is the part that can only
 * be verified on a real device.
 */

/**
 * The server's public identity, shipped in the client bundle.
 *
 * Public by design and safe to commit, exactly like `firebaseConfig`. It is
 * half of a keypair whose PRIVATE half lives in Secret Manager and never
 * leaves the Cloud Function — the pair is what lets a push service confirm
 * that a message claiming to come from Larder actually did. Publishing the
 * public half is how the scheme is meant to work.
 *
 * Empty until `npm run vapid` has been run and the value pasted into `.env`.
 * Empty means push is simply unavailable, which every caller already handles,
 * because that is also what an iOS browser tab looks like.
 */
export const VAPID_PUBLIC_KEY: string = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "";

/**
 * Can this browser, right now, receive a push at all?
 *
 * All three checks are load-bearing and fail on real devices in the wild:
 * `serviceWorker` is absent in a private window in some browsers,
 * `PushManager` is absent in every iOS browser TAB — it appears only once the
 * app is on the home screen — and `Notification` is absent on a handful of
 * embedded webviews that otherwise look like Chrome.
 */
export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Permission as the browser currently sees it, folded together with support. */
export function pushPermission(): PushPermission {
  if (!pushSupported()) return "unsupported";
  // No VAPID key means the server could not send one even with permission, so
  // asking would be collecting a promise we cannot keep.
  if (!VAPID_PUBLIC_KEY) return "unsupported";
  const p = Notification.permission;
  return p === "granted" || p === "denied" ? p : "default";
}

/**
 * The subscription this device already holds, if any. Never prompts.
 *
 * Used on launch to decide whether anything needs healing, and to answer
 * "am I subscribed?" in Settings without side effects.
 */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Ask the browser for permission, then subscribe.
 *
 * Fires the NATIVE dialog, so it must only ever be called from a user gesture
 * that follows our own soft ask — see the header of `ask.ts` for why the order
 * matters. Returns null on any refusal or failure; the caller treats every
 * failure the same way, because from the user's side they are the same thing.
 */
export async function requestAndSubscribe(): Promise<NewPushSubscription | null> {
  if (!pushSupported() || !VAPID_PUBLIC_KEY) return null;

  /**
   * iOS requires this call to happen inside the gesture that triggered it.
   * Awaiting anything BEFORE `requestPermission` — a Firestore read, a
   * registration lookup — can break that association and the dialog silently
   * never appears. So permission is asked first, before any other await.
   */
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return null;
  }
  if (permission !== "granted") return null;

  return subscribeNow();
}

/**
 * Subscribe with permission already granted. Does not prompt.
 *
 * Split from `requestAndSubscribe` because the launch-time heal path must never
 * be able to raise a dialog: a user who granted permission months ago should
 * not see anything when their subscription quietly expires and is replaced.
 */
export async function subscribeNow(): Promise<NewPushSubscription | null> {
  if (!pushSupported() || !VAPID_PUBLIC_KEY) return null;
  if (Notification.permission !== "granted") return null;

  try {
    const reg = await navigator.serviceWorker.ready;
    /**
     * `userVisibleOnly` is not optional in Chromium — subscribing without it
     * throws. It is the promise that every push results in something the user
     * can see, which `push-sw.js` keeps on every branch.
     */
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    return toRecord(sub);
  } catch {
    // Subscribing fails for reasons the user cannot act on — no network, a
    // push service outage, a key that changed since the last subscription.
    // The launch-time heal simply tries again next time.
    return null;
  }
}

/** Drop the subscription — both at the browser and, by the caller, in Firestore. */
export async function unsubscribe(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    // Already gone at the browser's end. The stored record is what matters and
    // the caller removes it regardless.
  }
  return endpoint;
}

/**
 * Flatten a `PushSubscription` into something Firestore can hold.
 *
 * `toJSON()` rather than reading `.keys` — the keys are only exposed through
 * `getKey()` as raw ArrayBuffers, and `toJSON()` is the one API that hands back
 * the base64url the server actually needs.
 */
export function toRecord(sub: PushSubscription): NewPushSubscription | null {
  const json = sub.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    ua: typeof navigator === "undefined" ? "" : navigator.userAgent.slice(0, 300),
    createdAt: Date.now(),
    // No `lastOkAt`. The sender owns it; writing it here would reset the
    // delivery record on every heal. See its note in shared/model/push.ts.
  };
}

/**
 * base64url → the `Uint8Array` that `applicationServerKey` demands.
 *
 * VAPID keys are published base64url (`-` and `_`, no padding) and this API
 * predates browsers accepting a string, so the conversion is on us.
 */
function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Backed by an explicit ArrayBuffer: `applicationServerKey` will not accept a
  // view that TypeScript believes might sit on a SharedArrayBuffer.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
